import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FilaProcessamentos } from '../components/common/FilaProcessamentos';
import { getProcessamento } from '../components/screens/epis/guidelines/config/processamentos';

const STORAGE_KEY = 'processamentos:acompanhando';
const INTERVALO_MS = 3000;
const FINALIZADOS = ['concluido', 'erro', 'expirado'];

export const ROTA_GRUPOS = '/epis/diretrizes/ghes';

const ProcessamentosContext = createContext(null);

function read() {
    try {
        const valor = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
        return Array.isArray(valor) ? valor : [];
    } catch {
        return [];
    }
}

function save(itens) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(itens));
    } catch {
        return;
    }
}

function extrairProcessamento(resposta) {
    const corpo = resposta?.data ?? resposta;

    if (corpo?.status) return corpo;
    if (corpo?.data?.status) return corpo.data;

    return null;
}

export function ProcessamentosProvider({ children }) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [itens, setItens] = useState(read);

    useEffect(() => {
        save(itens);
    }, [itens]);

    useEffect(() => {
        const sincronizar = (evento) => {
            if (evento.key === STORAGE_KEY) setItens(read());
        };

        window.addEventListener('storage', sincronizar);

        return () => window.removeEventListener('storage', sincronizar);
    }, []);

    const remover = useCallback((id) => {
        setItens((atuais) => atuais.filter((item) => String(item.id) !== String(id)));
    }, []);

    const fechar = useCallback((item) => {
        if (item.status === 'processando') {
            setItens((atuais) => atuais.map((i) => (String(i.id) === String(item.id) ? { ...i, oculto: true } : i)));
            return;
        }

        remover(item.id);
    }, [remover]);

    const acompanhar = useCallback(({ id, id_grupo, nome_grupo, id_empresa, acao = 'atualizar' }) => {
        if (id == null) return;

        const chave = String(id);

        setItens((atuais) => [
            {
                id: chave,
                id_grupo: id_grupo != null ? Number(id_grupo) : null,
                nome_grupo: nome_grupo ?? null,
                id_empresa: id_empresa ?? null,
                acao,
                status: 'processando',
                progresso: null,
                erro: null,
                oculto: false,
            },
            ...atuais.filter((item) => String(item.id) !== chave),
        ]);

        queryClient.invalidateQueries({ queryKey: ['processamentosAtivos'] });
    }, [queryClient]);

    const limpar = useCallback(() => {
        setItens([]);
    }, []);

    const abrirGrupo = useCallback((item) => {
        if (!item?.id_grupo) return;

        const params = new URLSearchParams({ page: '1', perPage: '10', editar: String(item.id_grupo) });

        if (item.id_empresa) params.set('id_empresa', String(item.id_empresa));
        if (item.nome_grupo) params.set('nome', item.nome_grupo);

        navigate(`${ROTA_GRUPOS}?${params.toString()}`);
    }, [navigate]);

    const pendentes = useMemo(() => itens.filter((item) => item.status === 'processando'), [itens]);

    const consultas = useQueries({
        queries: pendentes.map((item) => ({
            queryKey: ['processamento', item.id],
            queryFn: async () => {
                try {
                    const resposta = await getProcessamento(item.id);
                    return extrairProcessamento(resposta) ?? { id: item.id, status: 'processando' };
                } catch (erro) {
                    if (erro?.response?.status === 404) {
                        return { id: item.id, status: 'expirado', progresso: 100 };
                    }

                    throw erro;
                }
            },
            refetchInterval: (query) => (FINALIZADOS.includes(query.state.data?.status) ? false : INTERVALO_MS),
            refetchIntervalInBackground: true,
            refetchOnWindowFocus: false,
            retry: 2,
        })),
    });

    const resultados = consultas.map((consulta) => consulta.data).filter(Boolean);
    const assinatura = resultados.map((r) => `${r.id}:${r.status}:${r.progresso}`).join('|');
    const resultadosRef = useRef(resultados);
    resultadosRef.current = resultados;

    useEffect(() => {
        const atuais = resultadosRef.current;

        if (!atuais.length) return;

        setItens((lista) => lista.map((item) => {
            const dados = atuais.find((r) => String(r.id) === String(item.id));

            if (!dados || item.status !== 'processando') return item;

            const terminou = FINALIZADOS.includes(dados.status);

            return {
                ...item,
                status: dados.status,
                progresso: dados.progresso ?? item.progresso,
                erro: dados.erro ?? null,
                oculto: terminou ? false : item.oculto,
            };
        }));

        if (atuais.some((r) => FINALIZADOS.includes(r.status))) {
            queryClient.invalidateQueries({ queryKey: ['reportOfGHEs'] });
            queryClient.invalidateQueries({ queryKey: ['processamentosAtivos'] });
        }
    }, [assinatura, queryClient]);

    const abrirERemover = useCallback((item) => {
        abrirGrupo(item);
        remover(item.id);
    }, [abrirGrupo, remover]);

    const value = useMemo(
        () => ({ itens, acompanhar, remover, limpar, abrirGrupo }),
        [itens, acompanhar, remover, limpar, abrirGrupo]
    );

    return (
        <ProcessamentosContext.Provider value={value}>
            {children}
            <FilaProcessamentos itens={itens} onClose={fechar} onOpen={abrirERemover} />
        </ProcessamentosContext.Provider>
    );
}

export function useProcessamentos() {
    const contexto = useContext(ProcessamentosContext);

    if (!contexto) {
        throw new Error('useProcessamentos precisa estar dentro de <ProcessamentosProvider>.');
    }

    return contexto;
}